import type { AuthConfigNanoId, ConnectedAccountNanoId } from '@composio/types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { AuthConfigNanoIdSchema } from '@composio/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { atom, useAtom } from 'jotai'
import { ArrowRight, InfoIcon, PlusIcon, UserCircle2Icon } from 'lucide-react'
// @ts-expect-error No types available for this library
import { ShimmerTitle } from 'react-shimmer-effects'

import type { PostConnectedAccountsResponse } from '~/generated/client'
import { QUERY_KEYS } from '~/constants/keys'
import { UpdateQuery } from '~/dataProcessor/hooks/global/router'
import { useUser } from '~/dataProcessor/hooks/user'
import { Button } from '~/design-system/atom/button'
import { CodeBlockComponent } from '~/design-system/atom/CodeBlockComponent'
import { DialogFooter } from '~/design-system/atom/dialog'
import { Input } from '~/design-system/atom/input'
import { ToolTipBox } from '~/design-system/atom/tooltip'
import { useToast } from '~/design-system/atom/use-toast'
import {
  getAuthConfigsByNanoid,
  getConnectedAccountsByNanoid,
  postConnectedAccounts,
} from '~/generated/client'
import { useGetAllConnections } from '~/hooks/query/connections'
import { useGetAllIntegrations } from '~/hooks/query/tools'
import { SimpleDropdown } from '~/ui/components/custom/dropdown/simple'
import { If } from '~/utils/reactComponent'
import { showNLetters, upperCaseFirstLetter } from '~/utils/string'
import { updateWizardFlowAtom } from '../wizard'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputField = any

interface FormFieldValue {
  value: string
  required: boolean
  error?: string
}
type FormValues = Record<string, FormFieldValue>

type ConnectionInfo = Partial<PostConnectedAccountsResponse>

interface StateInfo {
  index: number
  data: unknown // Consider defining a more specific type if possible
  authConfigId: AuthConfigNanoId
}

export const getRedirectURLFromBE = async (
  _appName: string,
  authConfigId: AuthConfigNanoId,
  data: Record<string, unknown>,
  userEntityId: string,
): Promise<PostConnectedAccountsResponse> => {
  // Explicitly cast the response type if the generated client doesn't provide it directly
  return postConnectedAccounts({
    requestBody: {
      auth_config: {
        id: authConfigId,
      },
      connection: {
        data: data,
        user_id: userEntityId,
      },
    },
  }) // Adjust cast as needed
}

export const getConnectionStatus = async (
  connectedAccountId: ConnectedAccountNanoId,
) => {
  return getConnectedAccountsByNanoid({ nanoid: connectedAccountId }) // Adjust cast as needed
}

export const CODE_SNIPPETS = {
  python: {
    getIntegration: `from composio import ComposioToolSet, App
toolset = ComposioToolSet(api_key="{{api_key}}")

integration = toolset.get_integration(id="{{integration id}}")
# Collect auth params from your users
print(integration.expectedInputFields)
`,
    createConnection: `connection_request = toolset.initiate_connection(
    integration_id=integration.id,connected_account_params={{your_data}},
    entity_id="{{user_entity_id}}",
)

# Redirect step require for OAuth Flow
print(connection_request.redirectUrl)
print(connection_request.connectedAccountId)
`,
  },
  javascript: {
    getIntegration: `import { OpenAIToolSet } from 'composio-core';
const toolset = new OpenAIToolSet({ apiKey: '{{api_key}}' });

const integration = await toolset.integrations.get({integrationId: '{{integration id}}'});
const expectedInputFields = await toolset.integrations.getRequiredParams(integration.id);
// Collect auth params from your users
console.log(expectedInputFields);`,
    createConnection: `const connectedAccount = await toolset.connectedAccounts.initiate({
    integrationId: integration.id,
    entityId: '{{user_entity_id}}',
connectionParams:{{your_data}}
});

// connected account properties:
// connectionStatus (string), connectedAccountId (string), redirectUrl (string | null)
console.log(connectedAccount.redirectUrl);`,
  },
}

export const entityIdAtom = atom<string>('default')

export const ConnectedAccountSetup = ({
  setOpen,
  authConfigId,
  appName,
  appLogo,
  isStartFromAppScreenAndHasIntegration,
}: {
  setOpen: (e: boolean) => void
  open?: boolean
  authConfigId: AuthConfigNanoId
  appName: string
  appLogo: string
  isStartFromAppScreenAndHasIntegration: boolean
}) => {
  const { user } = useUser()

  const apiKey = user?.api_key?.key ?? undefined

  const [, setWizardFlow] = useAtom(updateWizardFlowAtom)

  const onStatusChange = (status: {
    connectedAccountId: ConnectedAccountNanoId | undefined | null
    status: string
  }) => {
    if (status.connectedAccountId) {
      setWizardFlow({
        step: 3,
        data: {
          third: {
            connectedAccountId: status.connectedAccountId,
            actionName: null,
          },
        },
      })
    }
  }

  const goToFirstStep = () => {
    setWizardFlow({
      step: 1,
      data: {
        first: {
          appName: appName,
        },
      },
    })
  }

  const switchIntegration = (authConfigId: AuthConfigNanoId) => {
    setWizardFlow({
      step: 2,
      data: {
        second: {
          authConfigId: authConfigId,
        },
      },
    })
  }

  const [stateInfo, setStateInfo] = React.useState<StateInfo>({
    index: 0,
    data: {},
    authConfigId,
  })

  const updateQuery = UpdateQuery()
  const queryClient = useQueryClient()
  const { query } = useRouter()

  const [formState, setFormState] = useAtom(formValueAtom)
  const toast = useToast()

  // Fetch Auth Config data using the correct query key and function
  const { data: authConfigData } = useQuery({
    queryKey: QUERY_KEYS.getAccountIntegrationQueryKey(authConfigId),
    queryFn: () => getAuthConfigsByNanoid({ nanoid: authConfigId }),
    retry: false,
    enabled: !!authConfigId, // Ensure query only runs when connectorId is available
  })

  const [connectionInfo, setConnectionInfo] = React.useState<ConnectionInfo>({
    status: undefined,
    id: undefined,
    redirect_url: undefined,
  })

  const getRedirectURL = (
    authConfigId: AuthConfigNanoId,
    userEntityId: string,
  ) => {
    // Explicitly define the accumulator type
    const payload = Object.entries(formState.formValues).reduce<
      Record<string, string | undefined>
    >((acc, [key, field]) => {
      // No need for ts-ignore/expect-error if types are correct
      acc[key] = field.value
      return acc
    }, {})

    return getRedirectURLFromBE(appName, authConfigId, payload, userEntityId)
  }

  const apiReqCounterRef = useRef(0)

  const updateForm = async (userEntityId: string) => {
    if (connectionInfo.status === 'INITIATED') {
      toast.toast({
        title: 'Connection already initiated',
        description: 'Please wait for the connection to complete',
      })
      return
    }

    // Use the defined FormValues type
    const newFormValues: FormValues = Object.entries(
      formState.formValues,
    ).reduce<FormValues>(
      (acc, [key, field]) => {
        if (field.required && !field.value) {
          // Ensure the accumulator has the correct structure
          acc[key] = { ...field, error: 'This field is required' }
        } else {
          // Remove potential error if value is now present
          acc[key] = { ...field, error: undefined }
        }
        return acc
      },
      // Start with a copy of the existing formValues to preserve structure
      { ...formState.formValues },
    )

    setFormState({ formValues: newFormValues }) // Update state with the correctly typed object

    if (Object.values(newFormValues).some((field) => field.error)) {
      return
    }

    // Use optional chaining and nullish coalescing for safer access
    if (
      ['API_KEY', 'BASIC', 'BEARER_TOKEN', 'BASIC_WITH_JWT'].includes(
        authConfigData?.auth_scheme ?? '',
      )
    ) {
      getRedirectURL(authConfigId, userEntityId)
        .then((res) => {
          onStatusChange({ connectedAccountId: res.id, status: res.status })
          updateQuery('addConnectorWizard', 'false')
          setConnectionInfo(res)
          return
        })
        .catch((err) => {
          // Safely access error details
          const message =
            (err?.response?.data as { message?: string })?.message ??
            (err instanceof Error ? err.message : 'An unknown error occurred')
          toast.toast({
            variant: 'destructive',
            title: 'Error',
            description: message,
          })
        })

      return
    }

    try {
      const redirectUrlInfo = await getRedirectURL(authConfigId, userEntityId)
      setConnectionInfo(redirectUrlInfo)

      const windowObj = redirectUrlInfo.redirect_url
        ? window.open(redirectUrlInfo.redirect_url, '_blank')
        : null

      const interval = setInterval(() => {
        getConnectionStatus(redirectUrlInfo.id).then((res) => {
          if (res.status !== 'INITIATED' && res.status !== 'INITIALIZING') {
            onStatusChange({
              connectedAccountId: redirectUrlInfo.id,
              status: res.status,
            })
            clearInterval(interval)
            windowObj?.close()
            // Revalidate the connected accounts
            // Keeping 'as any' for now as router query type can be complex.
            queryClient.invalidateQueries(
              QUERY_KEYS.getConnectionsQueryKey(query),
            )
          }
        })

        apiReqCounterRef.current += 1

        if (apiReqCounterRef.current > 60) {
          setStateInfo({ index: 1, data: { status: 'failed' }, authConfigId })
          clearInterval(interval)
        }
      }, 2000)
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'An unknown error occurred'
      toast.toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      })
    }
  }

  const [selectedLanguage, setSelectedLanguage] = useState('python')

  // // This query was incorrect, removing it as authConfigData holds the integration details
  // const { data: integrationData } = useQuery({
  //   queryKey: QUERY_KEYS.getAccountIntegrationQueryKey(connectorId),
  //   queryFn: () => ({ connectorId }), // Incorrect query function
  //   retry: false,
  // });

  const [entityId, setEntityId] = useAtom(entityIdAtom)

  const transformedCodeSnippet = useMemo(() => {
    // Use authConfigData which contains the integration details
    const expectedFields = authConfigData?.expected_input_fields

    // Explicitly type the accumulator and field
    const object = expectedFields?.reduce<Record<string, string | undefined>>(
      (acc, field: InputField) => {
        // Use InputField type
        acc[field.name] =
          (formState.formValues[field.name]?.value ?? field.default) ||
          `your ${field.name}`
        return acc
      },
      {},
    )

    const integrationUUID = authConfigData?.uuid // Use uuid from authConfigData
    const snippets =
      CODE_SNIPPETS[selectedLanguage as keyof typeof CODE_SNIPPETS]

    // Handle potential undefined integrationUUID safely
    const integrationCode = snippets.getIntegration
      .replace(
        '{{integration id}}',
        integrationUUID ?? 'UNKNOWN_INTEGRATION_ID',
      )
      .replace('{{api_key}}', apiKey ?? 'your_api_key')

    let createConnectionCode = snippets.createConnection
      .replace(
        '{{integration id}}',
        integrationUUID ?? 'UNKNOWN_INTEGRATION_ID',
      )
      .replace('{{api_key}}', apiKey ?? 'your_api_key')
      .replace('{{user_entity_id}}', entityId)

    const isJS = selectedLanguage === 'javascript'
    if (isJS) {
      createConnectionCode = createConnectionCode.replace(
        `\nconnectionParams:{{your_data}}`,
        Object.keys(object ?? {}).length === 0
          ? ''
          : `
    connectionParams:${JSON.stringify(object, null, 7).replace('}', '    }')}`,
      )
    } else {
      createConnectionCode = createConnectionCode.replace(
        `connected_account_params={{your_data}},`,
        Object.keys(object ?? {}).length === 0
          ? ''
          : `\n    connected_account_params=${JSON.stringify(object, null, 2).replace('}', '  }')},`,
      )
    }

    return {
      getIntegration: integrationCode,
      createConnection: createConnectionCode,
    }
    // Depend on authConfigData instead of the removed integrationData
  }, [selectedLanguage, apiKey, authConfigData, formState.formValues, entityId])

  return (
    <React.Fragment>
      <div
        className={clsx(
          'grid  grid-cols-7 gap-10 border-t-[#64646434] py-[0px] pb-10 font-gilroy text-[13px] text-[#ecececa2]',
        )}
      >
        <div className="col-span-3 pl-10 pt-8">
          <div className="mb-2 mt-1 font-cera text-[16px] font-[500] text-black-500">
            Create a connected account to execute the action
          </div>

          <If condition={isStartFromAppScreenAndHasIntegration}>
            <div className="flex min-w-full items-center gap-2">
              <div className="mt-[12px] min-w-full overflow-hidden rounded-[16px] border-[0.5px] border-[#216dc4] bg-[#5ec7ff03] px-5 py-4 text-[#0c5973]">
                <div className="text-[16px] font-[500]">
                  Using existing integration for {appName}
                </div>
                <div className="mt-2 flex items-center gap-2 text-[14px]">
                  To create a new integration, click button below.
                </div>
                <div className="mt-4 flex justify-between">
                  <Button
                    size={32}
                    variant={'black'}
                    onClick={() => {
                      goToFirstStep()
                    }}
                  >
                    <PlusIcon
                      className="mr-1"
                      height={16}
                      width={16}
                      strokeWidth={1.2}
                    />
                    New integration
                  </Button>
                  <SelectIntegration
                    appName={appName}
                    onChange={(value) => {
                      switchIntegration(AuthConfigNanoIdSchema.parse(value))
                    }}
                    selectedValue={authConfigId}
                  />
                </div>
              </div>
            </div>
          </If>

          <div className="mt-1 flex flex-col gap-2">
            <FAQItem
              step={1}
              title="Retrieve the integration "
              isOpen={true}
              onClick={() => {}}
            >
              <div className="mt-[-4px] text-[14px]">
                Use the integration ID to fetch integration details
              </div>
            </FAQItem>

            <FAQItem
              step={2}
              title="Create a connected account for your user"
              isOpen={true}
              onClick={() => {}}
            >
              <div className="mb-1 mt-[-4px] text-[14px]">
                Python & Javascript code to connect a new account 👉
              </div>
              <div>
                {stateInfo.index === 0 ? (
                  <SetupFormWizard
                    authConfigId={authConfigId}
                    updateForm={updateForm} // Pass the function directly
                    appName={appName}
                    setOpen={setOpen}
                  />
                ) : (
                  <ConnectionStatus
                    connectionInfo={connectionInfo}
                    stateInfo={stateInfo} // Pass stateInfo
                    setOpen={setOpen.bind(this)}
                    appLogo={appLogo}
                  />
                )}
              </div>
            </FAQItem>
          </div>
        </div>

        <div className="col-span-4  flex flex-col gap-6 bg-[#fff] px-7 !pt-[28px]  pr-10">
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-gray-700">Language:</div>
            <div className="flex items-center gap-2">
              <SelectorButton
                logo="/assets/icon/python.svg"
                logoSize={28}
                isSelected={selectedLanguage === 'python'}
                setSelected={setSelectedLanguage}
                name="python"
              />
              <SelectorButton
                logo="/assets/icon/js.svg"
                logoSize={14}
                isSelected={selectedLanguage === 'javascript'}
                setSelected={setSelectedLanguage}
                name="javascript"
              />
            </div>
          </div>

          <CodeBlockComponent
            topBarContent={'Code for creating a connection for a user'}
            blocks={[
              {
                heading: '🔍 Get the integration',
                text: transformedCodeSnippet.getIntegration || '',
                language: selectedLanguage,
              },
              {
                heading: '🚀 Initiate a New Connection',
                text: transformedCodeSnippet.createConnection || '',
                language: selectedLanguage,
              },
            ]}
          />
        </div>
      </div>
    </React.Fragment>
  )
}

export const SelectorButton = ({
  isSelected,
  setSelected,
  name,
  logo = null,
  logoSize,
}: {
  isSelected: boolean
  setSelected: (e: string) => void
  name: string
  logo?: string | null
  logoSize?: number
}) => {
  const isPython = useMemo(() => {
    return name === 'python'
  }, [name])

  return (
    <div
      className={`flex h-[30px] min-w-[60px] cursor-pointer items-center justify-center gap-2 rounded-[8px] border-[.5px] px-2 py-1 text-black-400 hover:border-[#4078fc] hover:bg-[#286bff38] ${
        isSelected ? 'border-[#4078fc] bg-[#286bff38]' : ' border-grey-500'
      }`}
      onClick={() => setSelected(name)}
    >
      <If condition={!!logo}>
        <img
          src={logo!}
          alt="logo"
          width={logoSize}
          height={logoSize}
          className={isPython ? 'ml-[-4px]' : ''}
        />
      </If>
      <div
        className={`font-gilroy text-[13.5px] font-[400] tracking-[0.05px] ${isPython ? 'ml-[-10px]' : ''}`}
      >
        {upperCaseFirstLetter(name)}
      </div>
    </div>
  )
}

export const SelectIntegration = ({
  appName,
  onChange,
  selectedValue,
}: {
  appName: string
  onChange: (value: string) => void
  selectedValue: string
}) => {
  const { data: { items: connectors } = {}, refetch } = useGetAllIntegrations({
    page: 1,
    toolkitSlug: appName,
  })

  useEffect(() => {
    refetch()
  }, [refetch])

  return (
    <SimpleDropdown
      options={
        connectors?.map((connector) => ({
          name: connector.name,
          value: connector.id,
        })) as {
          name: string
          value: string
        }[]
      }
      onChange={(value) => {
        onChange(value as string)
      }}
      value={selectedValue}
      size={32}
      placeholder="Tool"
      className="w-[160px]"
    />
  )
}

export const FAQItem = ({
  title,
  children,
  isOpen,
  onClick,
  step,
}: {
  title: string
  children: React.ReactNode
  isOpen: boolean
  onClick: () => void
  step: number
}) => {
  return (
    <div className="border-b-[0.5px] border-gray-200 py-6">
      <div
        className="flex w-full items-center gap-3 text-left"
        onClick={onClick}
      >
        <span className="font-cera text-[16px] font-[500] text-black-500 ">
          <span className="mr-2">{step}.</span> {title}
        </span>
      </div>
      {isOpen && (
        <div className="ml-[25px] mt-2 text-[15px] leading-[2] tracking-[0.15px] text-black-400">
          {children}
        </div>
      )}
    </div>
  )
}

const ConnectionStatus = ({
  setOpen,
  connectionInfo,
  stateInfo, // Add stateInfo prop
  appLogo,
}: {
  setOpen: (e: boolean) => void
  appLogo: string
  connectionInfo: ConnectionInfo // Use defined type
  stateInfo: StateInfo // Use defined type
}) => {
  const { refetch } = useGetAllConnections()
  const router = useRouter()
  // Use stateInfo if needed, e.g., show error if stateInfo.data.status === 'failed'
  console.log('Connection Status State Info:', stateInfo)

  return (
    <div>
      <DialogFooter className="mt-2 flex w-full justify-start">
        <div className="flex gap-2">
          <Button
            className="h-[40px] flex-1 rounded-[12px] px-4 text-[#fff]"
            variant={'black'}
            onClick={(e) => {
              e.stopPropagation()
              setOpen(false)
              router.push(`/connection/${connectionInfo.id}?tab=triggers`)
            }}
          >
            Try triggers
          </Button>
          <Button
            className="h-[40px] flex-1 rounded-[12px] px-4"
            variant={'default'}
            onClick={(e) => {
              e.stopPropagation()
              setOpen(false)
              router.push(`/connection/${connectionInfo.id}?tab=actions`)
            }}
          >
            Try actions
          </Button>
        </div>
      </DialogFooter>
    </div>
  )
}

const formValueAtom = atom<{
  formValues: FormValues // Use defined type
}>({
  formValues: {},
})

const SetupFormWizard = ({
  authConfigId,
  updateForm,
  appName,
  setOpen, // Added missing setOpen prop
}: {
  authConfigId: AuthConfigNanoId
  updateForm: (userEntityId: string) => Promise<void> // Use specific function signature
  appName: string
  setOpen: (e: boolean) => void // Add setOpen prop type
}) => {
  const [entityId, setEntityId] = useAtom(entityIdAtom)
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEYS.getAccountIntegrationQueryKey(authConfigId),
    queryFn: () => getAuthConfigsByNanoid({ nanoid: authConfigId }),
    retry: false,
    enabled: !!authConfigId,
  })

  // const { query } = useRouter(); // query is unused

  const [formState, setFormState] = useAtom(formValueAtom)

  useEffect(() => {
    // Ensure data and expected_input_fields exist before reducing
    if (
      data?.expected_input_fields &&
      Array.isArray(data.expected_input_fields)
    ) {
      // Explicitly type the accumulator and field
      const newFormValues = data.expected_input_fields.reduce<FormValues>(
        (acc, field: InputField) => {
          // Restore explicit type
          // No need for ts-ignore if types are correct
          acc[field.name] = {
            value: field.default || '',
            required: field.required || false,
          }
          return acc
        },
        {}, // Start with an empty object
      )

      // Update state correctly, ensuring the structure matches FormValues
      setFormState({ formValues: newFormValues })
    } else {
      // Optionally clear form if data is not available
      setFormState({ formValues: {} })
    }
    // Only depend on data, setFormState is stable
  }, [data, setFormState])

  const hasExpectedInputField = data?.expected_input_fields?.length || 0

  return (
    <div
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      className=" py-5 pt-3"
    >
      {hasExpectedInputField && !isLoading ? (
        <>
          <div className="text-[13px] font-[400] text-black-400">
            {hasExpectedInputField
              ? `Fill auth params to connect`
              : `Try from Dashboard`}
          </div>
          <div className="flex flex-wrap gap-4 pb-6 pt-3">
            {/* Add explicit check here too */}
            {data?.expected_input_fields &&
              Array.isArray(data.expected_input_fields) &&
              data.expected_input_fields.map((field: InputField) => {
                // Restore explicit type
                return (
                  <FormFieldRenderer
                    title={field.displayName || field.display_name}
                    placeholder={`Enter ${field.displayName || field.display_name}`}
                    type={field?.type || 'string'}
                    description={field.description || ''}
                    isRequired={field.required || false}
                    value={formState.formValues?.[field.name]?.value ?? ''}
                    error={formState.formValues?.[field.name]?.error}
                    setValue={(value: string) =>
                      setFormState((prev) => {
                        // Create the updated entry for the specific field
                        const updatedEntry = {
                          ...(prev.formValues[field.name] ?? {}),
                          value,
                          required: !!field?.required,
                          error: undefined,
                        }

                        // Construct the new formValues object
                        const newFormValues: FormValues = {
                          ...prev.formValues,
                          [field.name]: updatedEntry as FormFieldValue, // Ensure the entry conforms to FormFieldValue
                        }

                        return { ...prev, formValues: newFormValues }
                      })
                    }
                    key={field.name}
                  />
                )
              })}
          </div>
        </>
      ) : null}

      {isLoading && <ShimmerTitle line={2} gap={10} variant={'secondary'} />}

      {!isLoading && (
        <div className="mt-0 flex w-full">
          <div className="flex flex-col  gap-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="text-[13px] ">Your User's ID</div>{' '}
                <ToolTipBox
                  content={
                    <div>
                      This is your user ID. You can find it in the URL when you
                      are logged in to the app.
                      <br /> For example, if your email is sam@openai.com, your
                      user ID would be sam%40openai.com.
                    </div>
                  }
                >
                  <InfoIcon className="h-3 w-3 " />
                </ToolTipBox>
              </div>
              <Input
                value={entityId}
                onChange={(e) => {
                  setEntityId(e.target.value)
                }}
                placeholder="Enter user ID"
                size={36}
                icon={
                  <UserCircle2Icon
                    className="ml-[-6px] h-4 w-4 "
                    strokeWidth={1.2}
                    color="#343434"
                  />
                }
                className="w-[240px]"
              />
            </div>

            <Button
              className=" mt-3 h-[40px] w-[fit-content] min-w-[120px] gap-2 rounded-[12px] px-4 text-[14px] font-[500]"
              variant={'default'}
              onClick={() => void updateForm(entityId)} // Call updateForm with entityId
              size={40}
            >
              Try connecting {entityId}'s {showNLetters(appName, 16)}{' '}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export const FormFieldRenderer = ({
  type,
  value,
  setValue,
  placeholder,
  isRequired,
  error,
  description,
  title = '',
}: {
  type: string | undefined // Allow undefined type from field.type
  value: string
  setValue: (value: string) => void
  placeholder: string | undefined
  isRequired: boolean
  description: string
  error?: string
  title?: string
}) => {
  if (type == 'string') {
    return (
      <div>
        <div className="mb-[2px] text-left text-[13px] font-[400] text-black-200">
          {title}
          {isRequired && <span className="ml-1  text-[#ff5b81] ">*</span>}
        </div>
        <div className="mb-3 text-left text-[12px] text-black-400">
          <ShowToolTipBoxIfMoreThan60Chars description={description} />
        </div>
        <Input
          className="w-[240px] flex-auto gap-1.5"
          type="text"
          placeholder={placeholder}
          size={40}
          value={value}
          onChange={(e) => {
            e.stopPropagation()
            setValue(e.target.value)
          }}
          onClick={(e) => {
            e.stopPropagation()
          }}
        />
        <div className="ml-1 mt-2 text-[12px] text-[#ff5b81]">{error}</div>
      </div>
    )
  }

  return null
}

export const ShowToolTipBoxIfMoreThan60Chars = ({
  description,
  n = 60,
}: {
  description: string
  n?: number
}) => {
  if (description.length > n) {
    return (
      <ToolTipBox content={<div className="max-w-[400px]">{description}</div>}>
        <div className="flex items-center gap-1">
          <span>{description.substring(0, 57) + '...'}</span>{' '}
          <InfoIcon className="h-3 w-3 " />
        </div>
      </ToolTipBox>
    )
  }
  return <>{description}</>
}
